import React, { useContext, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useNavigate } from 'react-router-dom';
import { KeyRound, Lock, Mail, ShieldCheck, Trash2 } from 'lucide-react';
import { UserContext } from '../App';
import { supabase } from '../utils/supabaseClient';
import { useModal } from '../context/ModalContext';
import { useIsDesktop } from '../hooks/useIsDesktop';
import MemberCenterLayout from '../components/common/MemberCenterLayout';

// 偵測平台並設定正確的 redirect URI（與 Login.js 的 OAuth 導向邏輯一致）
const getRedirectUri = () => {
  if (Capacitor.isNativePlatform()) {
    return 'com.jogather.app://callback';
  }
  return `${window.location.origin}/app/account-security`;
};

export default function AccountSecurity() {
  const navigate = useNavigate();
  const { session, setSession, setCurrentUser } = useContext(UserContext);
  const isDesktop = useIsDesktop();
  const { showAlert, showConfirm } = useModal();
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  const email = session?.user?.email || '';
  const provider = session?.user?.app_metadata?.provider || 'email';
  const isEmailLogin = provider === 'email';

  // 使用者點擊密碼重設信中的連結後，Supabase 會觸發 PASSWORD_RECOVERY 事件，
  // 此時才顯示「設定新密碼」表單
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setIsRecoveryMode(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSendResetEmail = async () => {
    if (!email) return;
    setIsSendingReset(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: getRedirectUri()
      });
      if (error) throw error;
      showAlert('已寄出', `重設密碼信已寄至 ${email}，請至信箱點擊連結繼續。`);
    } catch (error) {
      console.error('寄送密碼重設信失敗:', error);
      showAlert('錯誤', '寄送失敗，請稍後再試。');
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleSetNewPassword = async () => {
    if (newPassword.length < 6) {
      showAlert('提示', '密碼至少需要 6 個字元');
      return;
    }
    if (newPassword !== confirmPassword) {
      showAlert('提示', '兩次輸入的密碼不一致');
      return;
    }
    setIsSavingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      showAlert('成功', '密碼已更新！');
      setIsRecoveryMode(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('更新密碼失敗:', error);
      showAlert('錯誤', '更新失敗，請稍後再試。');
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const { error } = await supabase.rpc('delete_user');
      if (error) throw error;

      showAlert('成功', '帳號已徹底刪除。很高興為您服務過，期待與您再相見！');
      await supabase.auth.signOut();
      if (setSession) setSession(null);
      if (setCurrentUser) setCurrentUser(null);
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('刪除帳號失敗:', error);
      showAlert('錯誤', '刪除失敗，這可能因為權限不足或是網路問題。請稍後再試。');
    }
  };

  const fieldStyle = {
    boxSizing: 'border-box',
    width: '100%',
    minHeight: '46px',
    padding: '11px 14px',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    fontSize: '15px',
    outline: 'none',
    fontFamily: 'inherit'
  };

  return (
    <MemberCenterLayout title="帳號安全" subtitle="管理登入方式與密碼" activeKey="security">
      <div style={{ maxWidth: '720px', margin: '0 auto', display: 'grid', gap: '16px' }}>
        {/* 帳號資訊卡 */}
        <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
            <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: '#eef4f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Mail size={18} color="#4e8bca" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600' }}>登入信箱</div>
              <div style={{ fontSize: '15px', color: '#1f2937', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email || '—'}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: '#eef4f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <ShieldCheck size={18} color="#4e8bca" />
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600' }}>登入方式</div>
              <div style={{ fontSize: '15px', color: '#1f2937', fontWeight: '600' }}>
                {isEmailLogin ? 'Email ／ 密碼登入' : 'Google 帳號登入'}
              </div>
            </div>
          </div>
        </div>

        {/* 密碼重設 / 設定新密碼 */}
        {isEmailLogin && (
          <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <KeyRound size={18} color="#4e8bca" />
              <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: '#1f2937' }}>密碼</h2>
            </div>

            {!isRecoveryMode ? (
              <>
                <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#6b7280', lineHeight: '1.6' }}>
                  我們會寄送一封重設密碼信到你的信箱，點擊信中連結後即可在這裡設定新密碼。
                </p>
                <button
                  onClick={handleSendResetEmail}
                  disabled={isSendingReset}
                  style={{
                    width: isDesktop ? 'auto' : '100%',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    border: 'none',
                    background: isSendingReset ? '#cbd5e1' : '#4e8bca',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '700',
                    cursor: isSendingReset ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSendingReset ? '寄送中...' : '寄送密碼重設信'}
                </button>
              </>
            ) : (
              <>
                <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#059669', lineHeight: '1.6', fontWeight: '600' }}>
                  驗證成功！請設定你的新密碼。
                </p>
                <div style={{ display: 'grid', gap: '12px', marginBottom: '14px' }}>
                  <label style={{ display: 'grid', gap: '6px', fontSize: '13px', color: '#374151', fontWeight: '600' }}>
                    新密碼
                    <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="至少 6 個字元" style={fieldStyle} />
                  </label>
                  <label style={{ display: 'grid', gap: '6px', fontSize: '13px', color: '#374151', fontWeight: '600' }}>
                    確認新密碼
                    <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="再輸入一次" style={fieldStyle} />
                  </label>
                </div>
                <button
                  onClick={handleSetNewPassword}
                  disabled={isSavingPassword}
                  style={{
                    width: isDesktop ? 'auto' : '100%',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    border: 'none',
                    background: isSavingPassword ? '#cbd5e1' : '#4e8bca',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '700',
                    cursor: isSavingPassword ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSavingPassword ? '儲存中...' : '儲存新密碼'}
                </button>
              </>
            )}
          </div>
        )}

        {!isEmailLogin && (
          <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <Lock size={18} color="#94a3b8" style={{ flexShrink: 0, marginTop: '2px' }} />
            <p style={{ margin: 0, fontSize: '13px', color: '#6b7280', lineHeight: '1.6' }}>
              你是透過 Google 帳號登入，密碼由 Google 管理，請至 Google 帳號設定變更密碼。
            </p>
          </div>
        )}

        <section style={{ marginTop: '8px', padding: '20px', background: '#ffffff', border: '1px solid #fecaca', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }} aria-labelledby="danger-zone-title">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <Trash2 size={18} color="#dc2626" aria-hidden="true" />
            <h2 id="danger-zone-title" style={{ margin: 0, color: '#991b1b', fontSize: '1.05rem', fontWeight: '750' }}>注意操作</h2>
          </div>
          <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: '13px', lineHeight: '1.65' }}>
            刪除後，所有個人資料、活動與好友紀錄將永久消失，且無法復原。
          </p>
          <button
            type="button"
            onClick={() => showConfirm('刪除帳號', '所有個人資料、活動與好友紀錄將永久消失，此操作無法復原。確定要徹底刪除帳號嗎？', handleConfirmDelete)}
            style={{ width: isDesktop ? 'auto' : '100%', minHeight: '44px', padding: '11px 18px', color: '#b91c1c', background: '#fff7f7', border: '1px solid #fca5a5', borderRadius: '12px', fontFamily: 'inherit', fontSize: '14px', fontWeight: '750', cursor: 'pointer' }}
          >
            徹底刪除帳號
          </button>
        </section>
      </div>
    </MemberCenterLayout>
  );
}
